import React from "react";
import t from "../../../../../../i18n";
import { useQOI } from "../../../useQOI";
import { useCategories } from "../../../../../../api/useCategories";
import { stableColor } from "@ui";
import { colors } from "@ui/Theme";
import type { PointOptionsObject } from "highcharts";
import { DateTime } from "luxon";
import { useReparabilityMatrixPeriod } from "../useReparabilityMatrixPeriod";
import { useTranslations } from "../../../../../../api/useTranslations";
import type { DiagnosisReporting } from "../../../../../../api/useReporting";
import type { Reparability } from "../../../../../../api/Reparability";

const serieMatrix: Highcharts.SeriesBubbleOptions = {
  type: "bubble",
  yAxis: "internalGap",
  data: [],
  replicateInNavigator: false,
  showInLegend: true,
};

type MatrixPoint = {
  name: string;
  y: number;
  custom: {
    project?: {
      id: string;
      name: string;
      siteId: string | null;
      typeId: string | null;
    };
    failure: {
      id: string;
      name: string;
      systemId: string | null;
      equipmentName: string | null;
      equipmentTypeId: string | null;
      degradationTypeId: string | null;
      failureCategoryId: string | null;
      reparability: Reparability | null;
    };
  };
};

export function useSeries(reporting: DiagnosisReporting): Highcharts.SeriesBubbleOptions[] {
  const translations = useTranslations();
  const [qoi] = useQOI(reporting);
  const failureCategories = useCategories("FAILURE_CATEGORY");
  const [selectedReparabilityMatrixPeriod] = useReparabilityMatrixPeriod({});

  return React.useMemo(() => {
    if (!selectedReparabilityMatrixPeriod) return [];
    const from = DateTime.fromMillis(selectedReparabilityMatrixPeriod).startOf("month");
    const to = DateTime.fromMillis(selectedReparabilityMatrixPeriod).endOf("month");

    return [...failureCategories, null].map((failureCategoryId, i) => ({
      ...serieMatrix,
      id: failureCategoryId ?? "otherFailures",
      name:
        failureCategoryId ?? t("reporting.reparability_impact_matrix.chart.otherFailureCategory"),
      color: failureCategoryId ? stableColor(failureCategoryId) : colors.raw.carbon400,
      legendIndex: i,
      data: reporting.periods
        .filter(({ period }) => from.toMillis() <= period && period < to.toMillis())
        .flatMap(({ values }) => values)
        .reduce<PointOptionsObject[]>((acc, { projectId, qoiId, impacts }) => {
          if (reporting.qoiById[qoiId]?.name !== qoi?.name) return acc;

          const project = reporting.projectById[projectId];

          const points = impacts.reduce<MatrixPoint[]>(
            (acc, { failureId, reparability, value }) => {
              const failure = reporting.failureById[failureId];

              if (failure?.failureCategoryId !== failureCategoryId) return acc;

              return [
                ...acc,
                {
                  name:
                    (reparability ?? failure.reparability)
                      ? t("reparability", (reparability ?? failure.reparability)!, translations)
                      : t("reparability.none"),
                  y: value,
                  custom: {
                    project,
                    failure,
                  },
                },
              ];
            },
            [],
          );

          return [...acc, ...points];
        }, []),
    }));
  }, [failureCategories, reporting, qoi, selectedReparabilityMatrixPeriod, translations]);
}
